import { motion } from "framer-motion";
import { BadgeCheck, GraduationCap, Briefcase, HeartHandshake } from "lucide-react";

const highlights = [
  { icon: Briefcase, text: "20+ years of deep expertise in HR & people development" },
  { icon: BadgeCheck, text: "Certified in psychometric assessment instruments" },
  { icon: GraduationCap, text: "Authorised career exploration practitioner" },
  { icon: HeartHandshake, text: "A warm, 1-to-1 mentoring approach for every student" },
];

export default function AboutMentor() {
  return (
    <section id="mentor" className="py-20 sm:py-28 bg-secondary/50" data-testid="mentor-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-5 relative"
          >
            <div className="rounded-[2rem] overflow-hidden shadow-2xl shadow-primary/20 aspect-[4/5] max-w-md mx-auto">
              <img
                src="/images/founder.png"
                alt="Founder and career exploration practitioner of TheKhoj"
                className="w-full h-full object-cover"
                data-testid="mentor-photo"
              />
            </div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4, duration: 0.6 }}
              className="relative z-10 -mt-10 mx-auto w-fit bg-card border border-border rounded-2xl shadow-xl px-6 py-4 text-center whitespace-nowrap"
              data-testid="mentor-experience-badge"
            >
              <p className="font-display text-2xl font-extrabold text-primary">20+ Years</p>
              <p className="text-xs text-muted-foreground font-medium">People Development & HR Leadership</p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.5, duration: 0.6 }}
              className="max-w-md mx-auto mt-6 text-center"
              data-testid="mentor-bio-short"
            >
              <p className="font-display text-xl font-bold">Shree</p>
              <p className="text-xs font-mono uppercase tracking-[0.2em] text-accent font-semibold mt-1">Founder & Certified Career Exploration Practitioner</p>
              <p className="text-sm text-muted-foreground leading-relaxed mt-3">
                Passionate about youth development, Shree is a certified practitioner and people-development
                professional with 20+ years of deep expertise in the people development space — now devoted to
                helping young minds turn what they love into what they do.
              </p>
            </motion.div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-7"
          >
            <p className="text-xs font-mono uppercase tracking-[0.25em] text-primary font-semibold mb-4">About Your Mentor</p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-tight">
              The guide behind <span className="text-accent">TheKhoj</span>
            </h2>
            <p className="mt-6 text-muted-foreground text-base sm:text-lg leading-relaxed max-w-xl">
              TheKhoj is founded by a people-development professional who has spent over two decades helping individuals
              discover what they are truly good at — inside boardrooms, campuses and classrooms. With formal qualifications
              in HR and certifications in psychometric instruments, she brings the same science used by top organisations
              to something far more personal: your child's future.
            </p>

            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {highlights.map((h, i) => (
                <motion.div
                  key={h.text}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.15 + i * 0.08 }}
                  className="flex items-start gap-3 bg-card border border-border rounded-2xl p-4"
                  data-testid={`mentor-highlight-${i + 1}`}
                >
                  <span className="w-9 h-9 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
                    <h.icon className="w-4.5 h-4.5" />
                  </span>
                  <p className="text-sm font-medium leading-snug">{h.text}</p>
                </motion.div>
              ))}
            </div>

          </motion.div>
        </div>
      </div>
    </section>
  );
}
